import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Rating,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Stack,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Location {
  _id: string;
  name: string;
  description: string;
  difficulty: string;
  amenities: string[];
  reviews: Review[];
  averageRating: number;
}

interface Review {
  _id: string;
  rating: number;
  comment: string;
  userName: string;
  createdAt: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Locations: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<number>(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations`);
      const locationsWithReviews = await Promise.all(
        response.data.map(async (location: Location) => {
          const reviewsResponse = await axios.get(
            `${API_URL}/api/reviews/location/${location._id}`
          );
          const reviews = reviewsResponse.data;
          const averageRating =
            reviews.reduce((acc: number, review: Review) => acc + review.rating, 0) /
            (reviews.length || 1);
          return { ...location, reviews, averageRating };
        })
      );
      setLocations(locationsWithReviews);
      setFilteredLocations(locationsWithReviews);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  useEffect(() => {
    let filtered = [...locations];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (location) =>
          location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply difficulty filter
    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(
        (location) => location.difficulty === difficultyFilter
      );
    }

    // Apply rating filter
    if (ratingFilter > 0) {
      filtered = filtered.filter(
        (location) => location.averageRating >= ratingFilter
      );
    }

    setFilteredLocations(filtered);
  }, [searchTerm, difficultyFilter, ratingFilter, locations]);

  const handleDifficultyChange = (event: SelectChangeEvent) => {
    setDifficultyFilter(event.target.value);
  };

  const handleRatingChange = (event: SelectChangeEvent<number>) => {
    setRatingFilter(Number(event.target.value));
  };

  const handleViewOnMap = (locationId: string) => {
    navigate('/', { state: { selectedLocationId: locationId } });
  };

  const handleViewDetails = (locationId: string) => {
    navigate(`/locations/${locationId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Kayaking Locations
      </Typography>

      {/* Filters */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Locations"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                label="Difficulty"
                onChange={handleDifficultyChange}
              >
                <MenuItem value="all">All Difficulties</MenuItem>
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="challenging">Challenging</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Minimum Rating</InputLabel>
              <Select
                value={ratingFilter}
                label="Minimum Rating"
                onChange={handleRatingChange}
              >
                <MenuItem value={0}>Any Rating</MenuItem>
                <MenuItem value={1}>1+ Stars</MenuItem>
                <MenuItem value={2}>2+ Stars</MenuItem>
                <MenuItem value={3}>3+ Stars</MenuItem>
                <MenuItem value={4}>4+ Stars</MenuItem>
                <MenuItem value={5}>5 Stars</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Location Cards */}
      <Grid container spacing={3}>
        {filteredLocations.map((location) => (
          <Grid item xs={12} sm={6} md={4} key={location._id}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {location.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Rating
                    value={location.averageRating}
                    precision={0.5}
                    readOnly
                    size="small"
                  />
                  <Link
                    component="button"
                    variant="body2"
                    color="primary"
                    onClick={() => handleViewDetails(location._id)}
                    sx={{ ml: 1, textDecoration: 'none' }}
                  >
                    ({location.reviews.length} reviews)
                  </Link>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Difficulty: {location.difficulty}
                </Typography>
                <Typography variant="body2" paragraph>
                  {location.description}
                </Typography>
                {location.amenities && location.amenities.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {location.amenities.map((amenity, index) => (
                      <Chip
                        key={index}
                        label={amenity}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    ))}
                  </Stack>
                )}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => handleViewDetails(location._id)}
                >
                  View Details
                </Button>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => handleViewOnMap(location._id)}
                >
                  View on Map
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Locations; 